<script setup lang="ts">
import { ClaimLineStatus } from '@rialtic/types'

const props = defineProps<{
  status: ClaimLineStatus
  dot?: boolean
}>()

interface StatusStyle {
  text: string
  /* Statuses without a pill render as plain text */
  pill?: string
  dot?: string
}

const statuses: Record<ClaimLineStatus, StatusStyle> = {
  [ClaimLineStatus.NOT_PAYABLE]: {
    text: 'Not payable',
    pill: 'bg-warning-100 text-warning-800',
    dot: 'bg-warning-500',
  },
  [ClaimLineStatus.NO_INSIGHTS]: { text: 'No insights' },
  [ClaimLineStatus.NEW_LINE]: { text: 'New line' },
  [ClaimLineStatus.RESOLVED]: {
    text: 'Resolved',
    pill: 'bg-tertiary-75 text-tertiary-800',
    dot: 'bg-success-300',
  },
  [ClaimLineStatus.EXCLUDED]: {
    text: 'Excluded insight',
    pill: 'bg-neutral-100 text-neutral-800',
    dot: 'ring-1 ring-neutral-400',
  },
  [ClaimLineStatus.OBSERVATION]: {
    text: 'Observation',
    pill: 'bg-primary-50 text-primary-700',
    dot: 'bg-primary-200',
  },
  [ClaimLineStatus.INFLUENCING]: {
    text: 'Influencing',
    pill: 'bg-secondary-100 text-secondary-900',
    dot: 'bg-secondary-600',
  },
  [ClaimLineStatus.NOT_INFLUENCING]: {
    text: 'Not Influencing',
    pill: 'bg-tertiary-200 text-tertiary-900',
    dot: 'bg-tertiary-500',
  },
}

const style = computed(() => statuses[props.status])
</script>

<template>
  <span
    v-if="dot"
    class="inline-block size-3 rounded-full"
    :class="style?.dot"
    data-testid="StatusBar__dot"
  />
  <UBadge
    v-else-if="style?.pill"
    :class="style.pill"
    :label="style.text"
    size="sm"
    variant="soft"
    data-testid="StatusBar__pill"
  >
    <template #trailing>
      <span class="size-2 rounded-full" :class="style.dot" />
    </template>
  </UBadge>
  <span v-else class="text-xs">{{ style?.text }}</span>
</template>
